// The rate card: what one unit of a provider's work costs, and since when.
//
// Read-only in this revision. A price change is `POST /api/provider-rates/set`,
// which closes the standing period and opens the next in ONE call — and a form
// that got that wrong would leave a key with two prices or none. So the screen
// shows the command instead, and a person runs it.

import { useMemo, useState, type ReactNode } from "react";
import type { ProviderRate, UsageRateGap } from "@crm/shared";
import styles from "./RatesPage.module.css";
import {
  countBySource,
  groupRates,
  isOpenGap,
  isTokenUnit,
  keyLabel,
  matchesSearch,
  matchesSource,
  perMillionMinor,
  rateAttribution,
  rateCardSummaryLine,
  setRateCommand,
  summariseRates,
  type RateGroup,
  type RateSourceFilter,
} from "./rateCard.js";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { Notice, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { SearchInput } from "../../design/SearchInput.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { assertNever } from "../../lib/assertNever.js";
import { formatInstantDate, formatMonth, todayIsoDate } from "../../format/calendarDate.js";
import { formatCount, formatMoney } from "../../format/money.js";
import { ProviderCostFactsCard } from "./ProviderCostFactsCard.js";
import { mergeUsageCost, type UsageCostUnion } from "../usage/usageMerge.js";
import { useProviderRates, useUsageCost } from "../../api/queries.js";
import { useReportCurrency } from "../../format/reportCurrency.js";

/** The month the gaps are read for: the one we are in, which is the open one. */
function currentPeriod(): string {
  return todayIsoDate().slice(0, 7);
}

/** The half-open period a rate priced, as a reader reads it. */
function periodLabel(rate: ProviderRate): string {
  const from = formatInstantDate(rate.effective_from);
  return rate.effective_to === null
    ? `${from} → still in force`
    : `${from} → ${formatInstantDate(rate.effective_to)}`;
}

/**
 * The price per million tokens, for the units that are counted in tokens.
 *
 * Beside the exact per-unit price, never instead of it: the wire figure is a
 * decimal string because a per-token price has no exact double, and this is the
 * form every provider's own price list is written in.
 */
function PerMillion({ rate }: { rate: ProviderRate }): ReactNode {
  if (!isTokenUnit(rate.unit)) return <span className={styles.muted}>—</span>;

  const minor = perMillionMinor(rate.price_per_unit);
  if (minor === null) return <span className={styles.muted}>—</span>;
  // A price below a hundredth of a cent per million would round to $0.00, and a
  // zero that is not a zero is the one render this app refuses.
  if (minor === 0 && Number(rate.price_per_unit) > 0) {
    return <span>under $0.01 {rate.currency}</span>;
  }
  return <span>{formatMoney(minor, rate.currency)}</span>;
}

/**
 * Who wrote the price this row states, and when.
 *
 * A feed row carries its raw `external_ref` as the cell's title: that string is
 * what a person greps the feed's own log for, and it is too long for a column
 * of its own. A key with no price in force has nobody to name — the row's "In
 * force" cell already says why.
 */
function WhoSaidSo({ standing }: { standing: ProviderRate | null }): ReactNode {
  if (standing === null) return <span className={styles.muted}>—</span>;

  const said = rateAttribution(standing);
  const when = <span className={styles.muted}>{formatInstantDate(said.receivedAt)}</span>;

  switch (said.kind) {
    case "person":
      return (
        <span className={styles.attribution}>
          <Badge shape="tag" tone="brand">
            {said.label}
          </Badge>
          {when}
        </span>
      );
    case "feed":
      return (
        <span
          className={styles.attribution}
          {...(said.externalRef === null ? {} : { title: said.externalRef })}
        >
          <Badge shape="tag" tone="slate">
            {said.label}
          </Badge>
          {when}
        </span>
      );
    default:
      return assertNever(said, "rate attribution");
  }
}

function GapRow({ gap }: { gap: UsageRateGap }): ReactNode {
  return (
    <tr>
      <Td>{gap.provider}</Td>
      <Td>{gap.operation}</Td>
      <Td mono>{gap.model ?? "no model"}</Td>
      <Td mono nowrap>
        {gap.deployment_slug}
      </Td>
      <Td mono nowrap>
        {formatInstantDate(gap.first_seen_at)}
      </Td>
      {/* A count, never an amount: these facts went unpriced, so what they cost
          is exactly what nobody knows. */}
      <Td numeric mono>
        {formatCount(gap.facts)}
      </Td>
      <Td>
        <code className={styles.command}>{setRateCommand(gap)}</code>
      </Td>
    </tr>
  );
}

/**
 * The gaps, read fleet-wide for the month we are in.
 *
 * Through the SAME hook and the same merge the usage screens use, not a second
 * read of its own: a gap is a fact about the month's cost, and two code paths
 * that both decide what "unpriced" means would eventually disagree about the
 * same month. The 202 arm carries the previous answer, so a month being
 * recomputed still lists the gaps it knew about.
 */
function RateGaps(): ReactNode {
  const period = currentPeriod();
  const currency = useReportCurrency();
  const results = useUsageCost([
    { period, groupBy: "model", deploymentSlug: null, tenantSlug: null, currency },
  ]);
  const union = mergeUsageCost(
    results.flatMap((result) => (result.data === undefined ? [] : [result.data])),
  );
  const failed = results.find((result) => result.error !== null && result.error !== undefined);
  const loading = results.some((result) => result.isPending);

  return (
    <Card>
      <CardHeader
        title="Open rate gaps"
        description={`Models whose facts nothing could price in ${formatMonth(period)}. Each one makes the month's cost short by an amount nobody can state.`}
      />
      <RateGapsBody union={union} failure={failed?.error} loading={loading} />
    </Card>
  );
}

function RateGapsBody({
  union,
  failure,
  loading,
}: {
  union: UsageCostUnion;
  failure: unknown;
  loading: boolean;
}): ReactNode {
  if (failure !== null && failure !== undefined) {
    return (
      <CardBody>
        <ProblemMessage error={failure} action="load the open rate gaps" />
      </CardBody>
    );
  }

  if (union.report === null) {
    return (
      <CardBody>
        {loading || union.stale ? (
          <Spinner label="Reading the month…" />
        ) : (
          <Notice
            tone="info"
            title="The month is still being computed, so the gaps are not known yet."
          />
        )}
      </CardBody>
    );
  }

  const gaps = union.report.rate_gaps.filter(isOpenGap);
  if (gaps.length === 0) {
    return (
      <CardBody>
        <Notice tone="info" title="Every fact in the month has a price" />
      </CardBody>
    );
  }

  return (
    <Table label="Open rate gaps">
      <thead>
        <tr>
          <Th>Provider</Th>
          <Th>Operation</Th>
          <Th>Model</Th>
          <Th>Deployment</Th>
          <Th>First seen</Th>
          <Th numeric>Facts</Th>
          <Th>Price it with</Th>
        </tr>
      </thead>
      <tbody>
        {gaps.map((gap) => (
          <GapRow
            key={`${gap.deployment_slug}|${gap.provider}|${gap.operation}|${gap.model ?? ""}`}
            gap={gap}
          />
        ))}
      </tbody>
    </Table>
  );
}

function HistoryTable({ group }: { group: RateGroup }): ReactNode {
  return (
    <Table label={`History for ${keyLabel(group.key)}`}>
      <thead>
        <tr>
          <Th>Period</Th>
          <Th numeric>Price per unit</Th>
          <Th>Currency</Th>
        </tr>
      </thead>
      <tbody>
        {group.history.map((rate) => (
          <tr key={rate.id}>
            <Td nowrap>{periodLabel(rate)}</Td>
            <Td numeric mono>
              {rate.price_per_unit}
            </Td>
            <Td>{rate.currency}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function RateRow({
  group,
  expanded,
  onToggle,
}: {
  group: RateGroup;
  expanded: boolean;
  onToggle: () => void;
}): ReactNode {
  const standing = group.standing;

  return (
    <>
      <tr>
        <Td strong>{group.key.provider}</Td>
        <Td>{group.key.operation}</Td>
        <Td mono>{group.key.model ?? "any model"}</Td>
        <Td mono>{group.key.unit}</Td>
        <Td>{group.key.region ?? "any region"}</Td>
        <Td numeric mono>
          {/* The wire figure verbatim: `price_per_unit` is a decimal STRING, and
              re-formatting it through a float is how 0.0000008 becomes 0.000001. */}
          {standing === null ? "—" : standing.price_per_unit}
        </Td>
        <Td numeric>{standing === null ? "—" : <PerMillion rate={standing} />}</Td>
        <Td nowrap>
          {standing === null ? (
            <Badge shape="chip" tone="warn">
              No price in force
            </Badge>
          ) : (
            periodLabel(standing)
          )}
        </Td>
        <Td nowrap>
          <WhoSaidSo standing={standing} />
        </Td>
        <Td>
          {group.history.length > 0 && (
            <Button
              small
              variant="subtle"
              aria-label={`History for ${keyLabel(group.key)}`}
              aria-expanded={expanded}
              onClick={onToggle}
            >
              {group.history.length} earlier
            </Button>
          )}
        </Td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={10} className={styles.historyCell}>
            <HistoryTable group={group} />
          </td>
        </tr>
      )}
    </>
  );
}

export function RatesPage(): ReactNode {
  const query = useProviderRates();
  const [search, setSearch] = useState("");
  const [source, setSource] = useState<RateSourceFilter>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const rates = query.data?.items;
  const groups = useMemo(
    () => (rates === undefined ? [] : groupRates(rates, new Date().toISOString())),
    [rates],
  );

  // The chips count what the SEARCH left, so a count always states what the
  // chip beside it would show. A count over the whole card would send a reader
  // to an empty table.
  const found = groups.filter((group) => matchesSearch(group, search));
  const counts = countBySource(found);
  const shown = found.filter((group) => matchesSource(group, source));

  const sourceOptions: ReadonlyArray<SegmentedOption<RateSourceFilter>> = [
    { value: "all", label: `All (${formatCount(counts.all)})`, title: "Every rate key" },
    {
      value: "person",
      label: `A person (${formatCount(counts.person)})`,
      title: "Keys whose price in force somebody typed",
    },
    {
      value: "feed",
      label: `Feeds (${formatCount(counts.feed)})`,
      title: "Keys whose price in force a provider feed wrote",
    },
  ];

  // Only once the rates are read: a summary of nothing would say "0 rates in
  // force", which is a claim about the card rather than about the load.
  const summary = rates === undefined ? undefined : rateCardSummaryLine(summariseRates(groups));

  usePageChrome({
    title: "Rate card",
    ...(summary === undefined ? {} : { summary }),
  });

  return (
    <div className={styles.page}>
      <RateGaps />

      {/* Above the card, because it outranks it: a provider's own figure prices
          a usage fact first, and our card is what prices the rest. */}
      <ProviderCostFactsCard />

      <QueryBoundary query={query} label="the rate card">
        {(page) => (
          <>
            {page.items.length < page.total && (
              <Notice
                tone="warn"
                title={`This card shows ${formatCount(page.items.length)} of ${formatCount(page.total)} rate periods. The API caps a list, so a key's oldest periods may be missing from its history.`}
              />
            )}

            <Card>
              <CardHeader
                title="Rate card"
                description="One row per rate key, priced by the period in force today. The earlier periods sit under it, because a closed period is why last month's figure differs from this month's."
                actions={
                  <SearchInput
                    value={search}
                    onCommit={setSearch}
                    placeholder="Provider, operation, model or unit"
                    ariaLabel="Search the rate card"
                  />
                }
              />
              <div className={styles.filters}>
                <Segmented
                  label="Who said so"
                  variant="inset"
                  options={sourceOptions}
                  value={source}
                  onChange={setSource}
                />
              </div>
              <Table label="Rate card">
                <thead>
                  <tr>
                    <Th>Provider</Th>
                    <Th>Operation</Th>
                    <Th>Model</Th>
                    <Th>Unit</Th>
                    <Th>Region</Th>
                    <Th numeric>Price per unit</Th>
                    <Th numeric>Per million tokens</Th>
                    <Th>In force</Th>
                    <Th>Who said so</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <EmptyRow
                      colSpan={10}
                      title="No rates match"
                      description="Nothing on the rate card carries this text, or wrote it. A provider with no rate at all shows up in the gaps band above, not here."
                    />
                  ) : (
                    shown.map((group) => (
                      <RateRow
                        key={group.id}
                        group={group}
                        expanded={expanded === group.id}
                        onToggle={() => setExpanded(expanded === group.id ? null : group.id)}
                      />
                    ))
                  )}
                </tbody>
              </Table>
            </Card>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}
